import { FC, useEffect, useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Device, Call } from '@twilio/voice-sdk'
import {
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Select,
  DialogTitle,
  MenuItem,
  Badge,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  Divider,
  Drawer,
  Typography,
  Tooltip,
  useTheme
} from '@material-ui/core'
import { useSnackbar } from 'notistack'
import SettingsIcon from '@material-ui/icons/Settings'
import AddIcCallIcon from '@material-ui/icons/AddIcCall'
import useAuth from '../../hooks/useAuth'
import { RootState } from '../../store'
import { setSelectedCall, syncVoipData } from '../../slices/phone'
import Scrollbar from '../Scrollbar'
import {
  getCallTime,
  isElectron,
  userIsParticipant,
  getUserById,
  getContactById,
  getAnyMyDeviceParticipantSid
} from '../../utils/common'
import { SelectedCall } from './phone'
import { getTwilioToken, warmConferenceControl } from '../../utils/twilio'
import { Iconference } from '../../types/twilio'

const ttl = 3600000
const refreshBuffer = 60000
let electronListener = null
// let swRegistration = null
let ipcRenderer = null

let platformCallButtonListener = null

if (isElectron()) {
  ipcRenderer = window.require('electron').ipcRenderer
}
const PhoneBar: FC = () => {
  const theme = useTheme()
  const { user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const { auth0Token, accountId, australisId } = user
  const dispatch = useDispatch()
  const selectedCall = useSelector(
    (state: RootState) => state.phone.selectedCall
  )
  const contacts = useSelector((state: RootState) => state.contacts.contacts)
  const conferences = useSelector((state: RootState) => state.phone.conferences)
  const users = useSelector((state: RootState) => state.phone.users)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [inputDevices, setInputDevices] = useState([])
  const [inputDevice, setInputDevice] = useState('default')
  const [outputDevices, setOutputDevices] = useState([])
  const [outputDevice, setOutputDevice] = useState('default')
  const [ringDevice, setRingDevice] = useState('default')
  const [device, setDevice] = useState(null)
  const [calls, setCalls] = useState([])
  const [loading, setLoading] = useState(false)
  const [incomingCalls, setIncomingCalls] = useState([])
  const lastReportedTime = useRef(Date.now())

  const reorderConferences = () => {
    const copy = [...conferences]
    const foundIndex = copy.findIndex((conference) => {
      let isParticipant = false

      const ids = Object.keys(conference.liveParticipants)

      const participantSid = getAnyMyDeviceParticipantSid(
        conference.liveParticipants,
        australisId,
        conference.participants
      )
      if (participantSid) {
        isParticipant = true
      }

      return isParticipant
    })

    if (foundIndex !== -1) {
      const deleted = copy.splice(foundIndex, 1)
      copy.unshift(...deleted)
    }

    return copy
  }

  const sortedConferences = conferences.length ? reorderConferences() : []

  const removeIncomingCall = (conferenceName: string) => {
    setIncomingCalls(
      incomingCalls.filter((inc) => inc.conferenceName !== conferenceName)
    )
  }

  const setCallTime = (cs?: Array<Iconference>) => {
    if (incomingCalls.length > 0) {
      incomingCalls.forEach((c) => {
        if (c.getStatus() !== 'pending') {
          removeIncomingCall(c.conferenceName)
        }
      })
    }
    if (cs) {
      setCalls(cs.map((call) => ({ ...call, elapsed: getCallTime(call) })))
    } else {
      setCalls(calls.map((call) => ({ ...call, elapsed: getCallTime(call) })))
    }
  }

  useEffect(() => {
    let myActiveConf = null
    const mappedConferences = sortedConferences.map((conf) => {
      const matchedIncoming = incomingCalls.find(
        (i) => i.conferenceName === conf.name
      )
      Object.keys(conf.liveParticipants).forEach((p) => {
        if (userIsParticipant(australisId, p)) {
          myActiveConf = conf
        }
      })
      if (matchedIncoming) {
        return { ...conf, incoming: true, ...matchedIncoming }
      }
      return conf
    })
    if (myActiveConf) {
      dispatch(setSelectedCall(myActiveConf.conferenceSid))
    }
    setCallTime(mappedConferences)
  }, [conferences, incomingCalls])

  useEffect(() => {
    if (selectedCall && selectedCall !== 'new') {
      warmConferenceControl(auth0Token, accountId, australisId, selectedCall)
    }
    const interval = setInterval(async () => {
      if (selectedCall && selectedCall !== 'new') {
        await warmConferenceControl(
          auth0Token,
          accountId,
          australisId,
          selectedCall
        )
      }
    }, 1000 * 60)
    return () => clearInterval(interval)
  }, [selectedCall])

  const setupDevice = async () => {
    setLoading(true)
    if (device) {
      device.removeAllListeners()
    }
    dispatch(syncVoipData(auth0Token, accountId, australisId))
    try {
      const twilioToken = await getTwilioToken(
        auth0Token,
        accountId,
        australisId
      )
      const d = new Device(twilioToken.token, {
        appName: 'Corvum Web',
        maxAverageBitrate: 51000,
        codecPreferences: [Call.Codec.Opus],
        allowIncomingWhileBusy: true,
        closeProtection:
          'There is an onging call, are you sure you wish to leave?'
      })
      setDevice(d)
      setInterval(async () => {
        try {
          const newToken = await getTwilioToken(
            auth0Token,
            accountId,
            australisId
          )
          d.updateToken(newToken.token)
        } catch (err) {
          enqueueSnackbar(
            `Error initializing your webphone, please reload the page or contact support. (Error: ${err})`,
            {
              anchorOrigin: {
                horizontal: 'right',
                vertical: 'bottom'
              },
              variant: 'error'
            }
          )
        }
      }, ttl - refreshBuffer)
      setLoading(false)
    } catch (err) {
      setLoading(false)
      enqueueSnackbar(
        `Error initializing your webphone, please reload the page or contact support. (Error: ${err})`,
        {
          anchorOrigin: {
            horizontal: 'right',
            vertical: 'bottom'
          },
          variant: 'error'
        }
      )
    }
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setCallTime()
    }, 1000)
    return () => clearInterval(interval)
  }, [calls])

  useEffect(() => {
    // navigator.serviceWorker
    //   .register('sw.js')
    //   .then((reg) => {
    //     console.log('sw reg')
    //     swRegistration = reg
    //   })
    //   .catch((err) => console.log(err))
    if (!device) {
      setupDevice()
    }
    const idleTimerInterval = setInterval(() => {
      const now = Date.now()
      if (now - lastReportedTime.current > 1000 * 60 * 60) {
        enqueueSnackbar(
          'Your application has been in a suspended state for an extended period of time, refreshing data now...',
          {
            anchorOrigin: {
              horizontal: 'right',
              vertical: 'bottom'
            },
            variant: 'info'
          }
        )
        setTimeout(() => {
          window.location.reload()
        }, 3000)
      }
      lastReportedTime.current = now
    }, 2000)
    return () => {
      clearInterval(idleTimerInterval)
    }
  }, [])

  useEffect(() => {
    if (device) {
      device.on('registering', () => {})
      device.on('error', (e) => {})
      device.on('registered', () => {
        device.audio.on('deviceChange', (e) => {
          const inputs = device.audio.availableInputDevices
          const outputs = device.audio.availableOutputDevices
          setInputDevices(inputs)
          setOutputDevices(outputs)
          const existingInput = inputs ? inputs.get(inputDevice) : null
          const existingOutput = outputs
            ? outputs.get(outputDevice) && outputs.get(ringDevice)
            : null
          if (!existingInput) {
            setInputDevice('default')
          }
          if (!existingOutput) {
            device.audio.speakerDevices.set('default')
            setOutputDevice('default')
            device.audio.ringtoneDevices.set('default')
            setRingDevice('default')
          }
        })
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then((stream) => {
            const inputs = device.audio.availableInputDevices
            const outputs = device.audio.availableOutputDevices
            setInputDevices(inputs)
            setOutputDevices(outputs)
            const savedInput = localStorage.getItem('inputDevice')
            if (savedInput) {
              const deviceExists = inputs ? inputs.has(savedInput) : null
              if (deviceExists) {
                setInputDevice(savedInput)
              } else {
                setInputDevice('default')
              }
            } else {
              setInputDevice('default')
            }
            const savedOutput = localStorage.getItem('outputDevice')
            if (savedOutput) {
              const deviceExists = outputs ? outputs.has(savedOutput) : null
              if (deviceExists) {
                setOutputDevice(savedOutput)
              } else {
                setOutputDevice('default')
              }
            } else {
              setOutputDevice('default')
            }
            const savedRing = localStorage.getItem('ringDevice')
            if (savedRing) {
              const deviceExists = outputs ? outputs.has(savedRing) : null
              if (deviceExists) {
                setRingDevice(savedRing)
                device.audio.ringtoneDevices.set(savedRing)
              } else {
                setRingDevice('default')
              }
            } else {
              setRingDevice('default')
            }
            // Calling getUserMedia will start the media track selected.
            // This is not desired as the user may get the impression the mic is in use.
            // Therefore, we want to avoid having tracks started when they're not needed.
            // We only wanted to get the input device list so we stop the tracks immediately.
            stream.getTracks().forEach((track) => track.stop())
          })
          .catch((error) => {
            console.error(error)
            if (isElectron()) {
              ipcRenderer
                .invoke('requestAction', {
                  action: 'requestMicAccess'
                })
                .then((res: string) => {
                  if (res !== 'granted') {
                    enqueueSnackbar(
                      'Please allow access to the microphone for calling functionality',
                      {
                        anchorOrigin: {
                          horizontal: 'right',
                          vertical: 'bottom'
                        },
                        variant: 'error'
                      }
                    )
                  }
                })
            } else {
              enqueueSnackbar(
                `Error connecting to your audio device, please check your devices and reload the page or contact support. (Error: ${error})`,
                {
                  anchorOrigin: {
                    horizontal: 'right',
                    vertical: 'bottom'
                  },
                  variant: 'error'
                }
              )
            }
          })

        if (isElectron()) {
          electronListener = async (e) => {
            const number = e.detail
            if (device) {
              try {
                device.connect({
                  params: { To: number }
                })
                enqueueSnackbar('Please wait while your call is initialized', {
                  anchorOrigin: {
                    horizontal: 'right',
                    vertical: 'top'
                  },
                  variant: 'success'
                })
              } catch (err) {
                enqueueSnackbar(
                  `There was an error initiating the call, please try again or contact support with these details. (Error: ${err})`,
                  {
                    anchorOrigin: {
                      horizontal: 'right',
                      vertical: 'bottom'
                    },
                    variant: 'error'
                  }
                )
              }
            }
          }
          navigator.serviceWorker.addEventListener('message', (e) => {})
          document
            .getElementById('root')
            .addEventListener('tel', electronListener)
        }

        platformCallButtonListener = async (e) => {
          const number = e.detail
          if (device) {
            try {
              device.connect({
                params: { To: number }
              })
              enqueueSnackbar('Please wait while your call is initialized', {
                anchorOrigin: {
                  horizontal: 'right',
                  vertical: 'top'
                },
                variant: 'success'
              })
            } catch (err) {
              enqueueSnackbar(
                `There was an error initiating the call, please try again or contact support with these details. (Error: ${err})`,
                {
                  anchorOrigin: {
                    horizontal: 'right',
                    vertical: 'bottom'
                  },
                  variant: 'error'
                }
              )
            }
          }
        }

        document
          .getElementById('root')
          .addEventListener('callClick', platformCallButtonListener)

        // When handling incoming calls, use the device that was selected earlier
        device.on('incoming', (connection: Call) => {
          try {
            if (!connection.customParameters.get('conferenceName')) {
              throw new Error('Conference name missing from incoming call')
            }
            const confName = connection.customParameters.get('conferenceName')
            const callObject = {
              conferenceName: confName,
              accept: () => connection.accept(),
              reject: () => connection.reject(),
              getStatus: () => connection.status()
            }
            setIncomingCalls([callObject, ...incomingCalls])
            if (isElectron()) {
              const notif = new Notification('Incoming call', {
                body: 'You have a new incoming call in the CORVUM portal',
                tag: confName,
                renotify: true
              })
              // swRegistration.showNotification('Incoming call', {
              //   body: 'You have a new incoming call in the CORVUM portal',
              //   tag: confName,
              //   renotify: true,
              //   actions: [
              //     { action: 'answer', title: 'Answer' },
              //     { action: 'reject', title: 'Reject' }
              //   ],
              //   requireInteraction: true
              // })
            } else {
              const notif = new Notification('Incoming call', {
                body: 'You have a new incoming call in the CORVUM portal',
                tag: confName,
                renotify: true
              })
            }
          } catch (error) {
            console.error(error)
            enqueueSnackbar(
              `Error connecting to your audio device, please check your devices and reload the page or contact support. (Error: ${error})`,
              {
                anchorOrigin: {
                  horizontal: 'right',
                  vertical: 'bottom'
                },
                variant: 'error'
              }
            )
          }
        })
        device.on('unregistered', () => {
          // device.register()
        })
      })
      if (device.state === 'unregistered' && device.state !== 'registering') {
        device.register()
      }
    }
    if (device) {
      if (isElectron()) {
        return () => {
          device.removeAllListeners()
          if (electronListener) {
            document
              .getElementById('root')
              .removeEventListener('tel', electronListener)
          }
        }
      }
      return () => device.removeAllListeners()
    }
    return null
  }, [device])

  const handleClick = (id: string) => {
    if (selectedCall === id) {
      dispatch(setSelectedCall(''))
    } else {
      dispatch(setSelectedCall(id))
    }
  }

  const getDeviceOptions = (devices) => {
    const list = []
    if (device) {
      devices.forEach((d) => {
        list.push({ value: d.deviceId, label: d.label })
      })
    }
    return list.map((item) => (
      <MenuItem key={item.value + 'select'} value={item.value}>
        {item.label}
      </MenuItem>
    ))
  }

  return (
    <>
      {/* Phone Bar */}
      <Drawer
        anchor="right"
        open
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            height: 'calc(100% - 64px) !important',
            top: '64px !Important',
            right: !!selectedCall ? 280 : 0,
            transition: 'right 225ms cubic-bezier(0, 0, 0.2, 1) 0ms',
            width: 80
          }
        }}
        variant="persistent"
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100%',
            maxWidth: 80,
            overflow: 'hidden'
          }}
        >
          <Tooltip title="New Call" placement="left">
            <span>
              <IconButton
                sx={{
                  margin: '5px',
                  padding: '5px',
                  backgroundColor: selectedCall === 'new' ? 'lightgray' : 'none'
                }}
                onClick={() => handleClick('new')}
                disabled={loading}
              >
                <Badge
                  badgeContent="Beta"
                  color="primary"
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                  }}
                  sx={{
                    mb: 0.5
                  }}
                >
                  <AddIcCallIcon fontSize="large" />
                </Badge>
                {loading && (
                  <CircularProgress
                    color="secondary"
                    size={40}
                    sx={{ position: 'absolute' }}
                  />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Divider style={{ width: '100%' }} />

          <Scrollbar
            supressMobile
            options={{ suppressScrollX: true }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingTop: 10,
              height: '100%'
            }}
          >
            {calls.map((call) => {
              const {
                liveParticipants,
                participants,
                conferenceSid,
                elapsed,
                incoming,
                dateCreated
              } = call

              const parts = Object.keys(liveParticipants)
              let firstParticipant = null
              for (const participant of parts) {
                const twiId = liveParticipants[participant]
                const details = participants[twiId]
                if (australisId !== details.australisId) {
                  let label = details.participantLabel
                  if (details.australisId) {
                    const u = getUserById(details.australisId, users)
                    if (u) {
                      label = u.australisId
                    }
                  }
                  if (details.contactId) {
                    const contact = getContactById(details.contactId, contacts)
                    if (contact) {
                      label = `${contact.firstName} ${contact.lastName}`
                    }
                  }
                  firstParticipant = label
                }
              }
              const liveCall = getAnyMyDeviceParticipantSid(
                liveParticipants,
                australisId,
                participants
              )
              return (
                <ButtonBase
                  sx={{
                    padding: '10px 20px',
                    marginBottom: '10px',
                    marginLeft: '5px',
                    borderRadius: '25px 0 0 25px',
                    width: '75px',
                    backgroundColor:
                      selectedCall === conferenceSid ? 'lightgray' : 'none'
                  }}
                  key={`${conferenceSid}-${dateCreated}-call`}
                  id={`${conferenceSid}-side`}
                  onClick={() => handleClick(conferenceSid)}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                    key={conferenceSid}
                  >
                    {parts.length > 1 ? (
                      <Badge
                        color="primary"
                        overlap="circular"
                        badgeContent={parts.length}
                      >
                        <Avatar
                          key={`${firstParticipant}-${conferenceSid}-avatar`}
                          className={incoming ? 'incoming-call-avatar' : ''}
                          sx={{
                            backgroundColor: liveCall
                              ? theme.palette.success.main
                              : ''
                          }}
                        />
                      </Badge>
                    ) : (
                      <Avatar
                        key={`${firstParticipant}-${conferenceSid}-avatar`}
                        className={incoming ? 'incoming-call-avatar' : ''}
                      />
                    )}
                    {liveCall ? (
                      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                        <Typography
                          sx={{ paddingTop: '3px' }}
                          variant="body2"
                          color={
                            selectedCall === conferenceSid
                              ? 'black'
                              : 'textPrimary'
                          }
                        >
                          {elapsed || '00:00'}
                        </Typography>
                        <Badge
                          className="blink"
                          color="secondary"
                          badgeContent=""
                          variant="dot"
                        />
                        <Divider />
                      </Box>
                    ) : (
                      <Typography
                        sx={{ paddingTop: '3px' }}
                        variant="body2"
                        color={
                          selectedCall === conferenceSid
                            ? 'black'
                            : 'textPrimary'
                        }
                      >
                        {elapsed || '00:00'}
                      </Typography>
                    )}
                  </Box>
                </ButtonBase>
              )
            })}
            {/* <ButtonBase
              sx={{
                padding: '10px 20px',
                marginBottom: '10px',
                borderRadius: '25px 0 0 25px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Avatar className="incoming-call-avatar" />
                <Typography sx={{ paddingTop: '3px' }} variant="body2">Incoming</Typography>
              </Box>
            </ButtonBase> */}
          </Scrollbar>

          <Divider style={{ width: '100%' }} />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 1
            }}
          >
            <Tooltip title="Audio Settings" placement="left">
              <span>
                <IconButton
                  disabled={loading}
                  onClick={() => setSettingsOpen(true)}
                >
                  <SettingsIcon fontSize="large" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Drawer>

      {/* Content */}
      <Drawer
        anchor="right"
        open={!!selectedCall}
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            height: 'calc(100% - 64px) !important',
            top: '64px !Important',
            width: 280
          }
        }}
        variant="persistent"
      >
        <Scrollbar
          supressMobile
          style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
          {selectedCall && (
            <SelectedCall
              call={
                selectedCall === 'new'
                  ? selectedCall
                  : calls.find((call) => call.conferenceSid === selectedCall)
              }
              contacts={contacts}
              users={users}
              device={device}
              inputDevice={inputDevice}
              removeIncomingCall={removeIncomingCall}
            />
          )}
        </Scrollbar>
      </Drawer>

      {/* Settings Pop-up */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)}>
        <DialogTitle>Audio settings</DialogTitle>
        <DialogContent sx={{ width: 500 }}>
          <DialogContentText>Input Device</DialogContentText>
          <Select
            variant="outlined"
            value={inputDevice}
            fullWidth
            onChange={(event) => {
              setInputDevice(event.target.value)
              localStorage.setItem('inputDevice', event.target.value)
            }}
          >
            {getDeviceOptions(inputDevices)}
          </Select>
          <div style={{ height: 10 }} />
          {device && !device.audio.isOutputSelectionSupported ? (
            <DialogContentText>
              Warning: The current browser does not support output selection
            </DialogContentText>
          ) : null}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <DialogContentText>Call Output Device</DialogContentText>
            <Button onClick={() => device.audio.speakerDevices.test()}>
              Play test sound
            </Button>
          </Box>
          <Select
            variant="outlined"
            value={outputDevice}
            fullWidth
            disabled={
              !device || (device && !device.audio.isOutputSelectionSupported)
            }
            onChange={(event) => {
              setOutputDevice(event.target.value)
              localStorage.setItem('outputDevice', event.target.value)
              if (device) {
                try {
                  device.audio.speakerDevices.set(event.target.value)
                } catch (err) {
                  enqueueSnackbar(`A problem occured. (Error: ${err})`, {
                    anchorOrigin: {
                      horizontal: 'right',
                      vertical: 'bottom'
                    },
                    variant: 'error'
                  })
                }
              }
            }}
          >
            {getDeviceOptions(outputDevices)}
          </Select>
          <div style={{ height: 10 }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <DialogContentText>Ringtone Output Device</DialogContentText>
            <Button onClick={() => device.audio.ringtoneDevices.test()}>
              Play test sound
            </Button>
          </Box>
          <Select
            variant="outlined"
            value={ringDevice}
            fullWidth
            disabled={
              !device || (device && !device.audio.isOutputSelectionSupported)
            }
            onChange={(event) => {
              setRingDevice(event.target.value)
              console.log(event.target.value)
              localStorage.setItem('ringDevice', event.target.value)
              if (device) {
                try {
                  device.audio.ringtoneDevices.set(event.target.value)
                } catch (err) {
                  enqueueSnackbar(`A problem occured. (Error: ${err})`, {
                    anchorOrigin: {
                      horizontal: 'right',
                      vertical: 'bottom'
                    },
                    variant: 'error'
                  })
                }
              }
            }}
          >
            {getDeviceOptions(outputDevices)}
          </Select>
          <div style={{ height: 10 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default PhoneBar
